
import React from 'react';
import { Client, ClientNewsAlert } from "../types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientInfo } from "./ClientInfo";
import { ContactsTab as ClientContactsTab } from "../contacts/ContactsTab";
import { ClientRecordsTab } from "./ClientRecordsTab";
import ClientJobsTab from "../jobs/ClientJobsTab";
import { ClientNewsAlertsTab } from "./ClientNewsAlertsTab";
import { ClientCommunicationTimeline } from "./ClientCommunicationTimeline";
import { ClientReportingTab } from "./ClientReportingTab";
import { ClientSatisfactionTab } from "./ClientSatisfactionTab";
import { EnrichmentResponse } from '@/hooks/useCompanyEnrichment';
import { ClientRevenueForecastTab } from "./ClientRevenueForecastTab";

interface ClientDisplayViewProps {
  client: Client;
  fieldsWithNewDataSet: Set<keyof Client>;
  lastEnrichmentData?: Partial<EnrichmentResponse> | null;
  handleEnrichClient: () => void;
  isEnrichmentTriggering: boolean;
  isEnrichmentPolling?: boolean;
  refetchClient: () => void;
}

// Type guard to check if value is ClientNewsAlert array
function isClientNewsAlertArray(value: unknown): value is ClientNewsAlert[] {
  return Array.isArray(value) && (value.length === 0 || (typeof value[0] === 'object' && value[0] !== null && 'id' in value[0]));
}

export const ClientDisplayView: React.FC<ClientDisplayViewProps> = ({
  client,
  fieldsWithNewDataSet,
  lastEnrichmentData,
  handleEnrichClient,
  isEnrichmentTriggering,
  isEnrichmentPolling,
  refetchClient,
}) => {
  // Safely access client_news_alerts with type checking
  const newsAlerts: ClientNewsAlert[] = isClientNewsAlertArray(client.client_news_alerts) 
    ? client.client_news_alerts 
    : [];

  return (
    <>
      <ClientInfo
        client={client}
        fieldsWithNewData={fieldsWithNewDataSet}
        handleEnrichClient={handleEnrichClient}
        isEnrichmentTriggering={isEnrichmentTriggering}
        isEnrichmentPolling={isEnrichmentPolling}
        lastEnrichmentData={lastEnrichmentData}
      />

      <Tabs defaultValue="timeline" className="mt-6">
        <TabsList>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="reporting">Reports</TabsTrigger>
          <TabsTrigger value="satisfaction">Satisfaction</TabsTrigger>
          <TabsTrigger value="forecast">Revenue Forecast</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="jobs">Jobs</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="news">News & Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-6">
          <ClientCommunicationTimeline 
            clientId={client.id} 
            clientName={client.name}
          />
        </TabsContent>

        <TabsContent value="reporting" className="mt-6">
          <ClientReportingTab 
            clientId={client.id} 
            clientName={client.name}
          />
        </TabsContent>

        <TabsContent value="satisfaction" className="mt-6">
          <ClientSatisfactionTab 
            clientId={client.id} 
            clientName={client.name}
          />
        </TabsContent>

        <TabsContent value="forecast" className="mt-6">
          <ClientRevenueForecastTab 
            clientId={client.id} 
            clientName={client.name}
          />
        </TabsContent>

        <TabsContent value="contacts" className="mt-6">
          <ClientContactsTab clientId={client.id} />
        </TabsContent>

        <TabsContent value="jobs" className="mt-6">
          <ClientJobsTab clientId={client.id} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <ClientRecordsTab clientId={client.id} />
        </TabsContent>

        <TabsContent value="news" className="mt-6">
          <ClientNewsAlertsTab
            clientId={client.id}
            newsAlerts={newsAlerts}
            newsMonitoringEnabled={client.news_monitoring_enabled}
            onUpdate={refetchClient}
          />
        </TabsContent>
      </Tabs>
    </>
  );
};
